/**
 * §13 `Measure` — the grapheme counter, implementation contract frozen by
 * contracts/adapter.md §9:
 *
 *   1. ASCII fast path:  /^[\x20-\x7E]*$/.test(s) ? s.length : …
 *   2. Memo, keyed by the string, for everything else
 *   3. Intl.Segmenter ONLY for a cold cluster scan of non-ASCII text
 *
 * NEVER per word. Measured on this project (node 24, desktop, 5,000 words):
 *
 *   Intl.Segmenter per word, fresh segmenter   100.1 ms
 *   Intl.Segmenter per word, shared segmenter   31.2 ms
 *   ASCII fast path + memo                       0.51 ms
 *
 * — which is the 82 ms figure in the contract reproduced, plus the finding that most of it
 * is Segmenter CONSTRUCTION. The memo still beats a shared segmenter by ~60×, so the frozen
 * design stands and the fast path is not the only reason it wins.
 *
 * `measure()` returns a bare number, so `drain()` is the ONLY channel by which "line widths
 * may be wrong for CJK/Thai" can reach the QC report. Callers drain after each pass.
 */

import type { Diagnostic, Measure, WidthModel } from '../../../contracts/cue-model.ts';
import {
  COMBINING_RE,
  HAS_PICTOGRAPHIC_RE,
  HAS_REGIONAL_INDICATOR_RE,
  hasTextPresentation,
  isWideCodePoint,
} from './width.ts';

export const DEFAULT_WIDTH_MODEL: WidthModel = {
  fullWidthCost: 2,
  halfWidthCost: 1,
  combiningCost: 0,
  emojiCost: 2,
};

/** Default memo cap. Beyond this the memo is reset wholesale rather than evicted one by one:
 *  an LRU costs more per lookup than the scan it saves, and the memo rebuilds in milliseconds. */
export const DEFAULT_MEMO_CAP = 50_000;

const ASCII_RE = /^[\x20-\x7E]*$/;

/** The slice of Intl.Segmenter this module uses. Narrow on purpose, so it can be stubbed. */
export interface GraphemeSegmenter {
  segment(input: string): Iterable<{ readonly index: number; readonly segment: string }>;
}

export interface MeasureOptions {
  /** Entries before the memo resets. */
  readonly memoCap?: number;
  /**
   * Injected segmenter. `undefined` = use `Intl.Segmenter` when the runtime has it.
   * `null` = force the fallback path, which is how the 'segmenter-fallback' diagnostic
   * is exercised in tests.
   */
  readonly segmenter?: GraphemeSegmenter | null;
}

function defaultSegmenter(): GraphemeSegmenter | null {
  const intl = (
    globalThis as { Intl?: { Segmenter?: new (l?: string, o?: object) => GraphemeSegmenter } }
  ).Intl;
  if (!intl?.Segmenter) return null;
  return new intl.Segmenter(undefined, { granularity: 'grapheme' });
}

/**
 * Cluster boundaries WITHOUT a segmenter. Approximate by design: it glues combining marks,
 * variation selectors, skin-tone modifiers, ZWJ sequences and regional-indicator pairs onto
 * the preceding base, which covers the cases a caption actually contains. It does NOT
 * implement Indic conjunct or Hangul jamo composition rules, so Devanagari and Thai cluster
 * counts can be too high — which is precisely what 'segmenter-fallback' warns about.
 */
function fallbackBoundaries(s: string): number[] {
  const out: number[] = [0];
  let i = 0;
  let prevWasRegional = false;
  while (i < s.length) {
    const cp = s.codePointAt(i) as number;
    const size = cp > 0xffff ? 2 : 1;
    const isRegional = cp >= 0x1f1e6 && cp <= 0x1f1ff;
    const glue =
      i > 0 &&
      (isCombiningCp(cp) ||
        cp === 0x200d || // ZWJ
        (cp >= 0xfe00 && cp <= 0xfe0f) || // variation selectors
        (cp >= 0x1f3fb && cp <= 0x1f3ff) || // skin-tone modifiers
        (isRegional && prevWasRegional) ||
        prevJoinedByZwj(s, i));
    if (!glue && i > 0) out.push(i);
    prevWasRegional = isRegional && !(isRegional && prevWasRegional);
    i += size;
  }
  out.push(s.length);
  return out;
}

const COMBINING_ONE_RE = /^[\p{Mn}\p{Me}]$/u;
function isCombiningCp(cp: number): boolean {
  return COMBINING_ONE_RE.test(String.fromCodePoint(cp));
}
function prevJoinedByZwj(s: string, i: number): boolean {
  return i >= 1 && s.charCodeAt(i - 1) === 0x200d;
}

/** Width of one grapheme cluster under a width model. */
export function clusterWidth(cluster: string, model: WidthModel): number {
  if (cluster === '') return 0;
  // U+FE0E asks for TEXT presentation, which is ONE cell. Checked before the emoji test,
  // because the cluster still contains an Extended_Pictographic code point and would otherwise
  // be charged two. Found while judging the noMisleadingCharacterClass finding on width.ts.
  if (hasTextPresentation(cluster)) return model.halfWidthCost;
  if (HAS_REGIONAL_INDICATOR_RE.test(cluster) || HAS_PICTOGRAPHIC_RE.test(cluster))
    return model.emojiCost;
  if (COMBINING_RE.test(cluster)) return model.combiningCost;
  const base = cluster.codePointAt(0) as number;
  return isWideCodePoint(base) ? model.fullWidthCost : model.halfWidthCost;
}

const SAME_AS_DEFAULT = (m: WidthModel): boolean =>
  m.fullWidthCost === DEFAULT_WIDTH_MODEL.fullWidthCost &&
  m.halfWidthCost === DEFAULT_WIDTH_MODEL.halfWidthCost &&
  m.combiningCost === DEFAULT_WIDTH_MODEL.combiningCost &&
  m.emojiCost === DEFAULT_WIDTH_MODEL.emojiCost;

export interface CoreMeasure extends Measure {
  /** Grapheme-CLUSTER count. The cps numerator; `measure` is the CELL count. */
  readonly clusters: (text: string) => number;
  /** Cluster count excluding whitespace clusters — CueMetrics.clustersNoSpace. */
  readonly clustersNoSpace: (text: string) => number;
}

/**
 * Builds a `Measure`. One per worker; it dies with a terminate and simply rebuilds.
 */
export function createMeasure(opts: MeasureOptions = {}): CoreMeasure {
  const memoCap = opts.memoCap ?? DEFAULT_MEMO_CAP;
  const segmenter = opts.segmenter === undefined ? defaultSegmenter() : opts.segmenter;

  // Two memos, both keyed by the raw string. `cells` is only populated for the DEFAULT width
  // model; a caller-supplied model recomputes from `bounds`, so a model swap can never read
  // a stale number out of a cache keyed on the string alone.
  const bounds = new Map<string, readonly number[]>();
  const cells = new Map<string, number>();
  let diagnostics: Diagnostic[] = [];
  let fallbackReported = false;

  const noteFallback = (): void => {
    if (fallbackReported) return;
    fallbackReported = true;
    diagnostics.push({
      code: 'segmenter-fallback',
      severity: 'warning',
      phase: 'validate',
      scope: {},
      message:
        'Intl.Segmenter is unavailable; grapheme clusters were approximated. ' +
        'Line widths may be wrong for Devanagari, Thai and other complex scripts.',
    });
  };

  const capCheck = (): void => {
    if (bounds.size + cells.size > memoCap) {
      bounds.clear();
      cells.clear();
    }
  };

  const computeBoundaries = (text: string): readonly number[] => {
    if (text === '') return [0];
    if (segmenter === null) {
      noteFallback();
      return fallbackBoundaries(text);
    }
    const out: number[] = [];
    for (const seg of segmenter.segment(text)) out.push(seg.index);
    out.push(text.length);
    return out;
  };

  const boundaries = (text: string): readonly number[] => {
    // The ASCII fast path applies here too: every printable-ASCII character is its own
    // cluster, so the boundaries are 0..length and no scan is needed.
    if (ASCII_RE.test(text)) {
      const out: number[] = [];
      for (let i = 0; i <= text.length; i++) out.push(i);
      return out;
    }
    const hit = bounds.get(text);
    if (hit !== undefined) return hit;
    const computed = computeBoundaries(text);
    capCheck();
    bounds.set(text, computed);
    return computed;
  };

  const measure = (text: string, model: WidthModel = DEFAULT_WIDTH_MODEL): number => {
    // 1. ASCII fast path — printable ASCII is one half-width cell per character.
    if (ASCII_RE.test(text)) return text.length * model.halfWidthCost;

    const isDefault = SAME_AS_DEFAULT(model);
    if (isDefault) {
      const hit = cells.get(text);
      if (hit !== undefined) return hit;
    }

    // 3. Cold cluster scan, once, then summed by the width model.
    const b = boundaries(text);
    let total = 0;
    for (let i = 0; i < b.length - 1; i++) {
      total += clusterWidth(text.slice(b[i] as number, b[i + 1] as number), model);
    }
    if (isDefault) {
      capCheck();
      cells.set(text, total);
    }
    return total;
  };

  const clusters = (text: string): number => {
    if (ASCII_RE.test(text)) return text.length;
    return boundaries(text).length - 1;
  };

  const clustersNoSpace = (text: string): number => {
    if (ASCII_RE.test(text)) {
      let n = 0;
      for (let i = 0; i < text.length; i++) if (text.charCodeAt(i) !== 0x20) n++;
      return n;
    }
    const b = boundaries(text);
    let n = 0;
    for (let i = 0; i < b.length - 1; i++) {
      const c = text.slice(b[i] as number, b[i + 1] as number);
      if (!/^\s+$/u.test(c)) n++;
    }
    return n;
  };

  return {
    measure,
    boundaries,
    clusters,
    clustersNoSpace,
    reset(): void {
      bounds.clear();
      cells.clear();
    },
    size(): number {
      return bounds.size + cells.size;
    },
    drain(): readonly Diagnostic[] {
      const out = diagnostics;
      diagnostics = [];
      fallbackReported = false;
      return out;
    },
  };
}
